import Popover, { PopoverProps } from 'client/components/core/Popover'
import React, { useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { ActionMenuButton, ActionMenuButtonProps } from './action-menu-button'

type ActionMenuItemIdExtended = string | number

export interface ActionMenuItem<Id extends ActionMenuItemIdExtended> {
  id: Id
  label: ActionMenuButtonProps['label']
  iconBefore?: ActionMenuButtonProps['iconBefore']
  iconAfter?: ActionMenuButtonProps['iconAfter']
}

export interface ActionMenuProps<
  Id extends ActionMenuItemIdExtended,
  MenuItem extends ActionMenuItem<Id> = ActionMenuItem<Id>,
> {
  items: MenuItem[]
  onSelect: (item: Id) => void
  triggerElement: (
    setIsOpened: (isOpened: boolean) => void,
  ) => React.ReactElement
  popoverProps?: Partial<PopoverProps>
}

export const ActionMenu = <Id extends ActionMenuItemIdExtended>({
  items,
  onSelect,
  triggerElement,
  popoverProps,
}: ActionMenuProps<Id>) => {
  const [isOpened, setIsOpened] = useState(false)
  return (
    <Popover
      ignoredEventTypes={['focusout']}
      isOpen={isOpened}
      setIsOpen={setIsOpened}
      offsetValue={10}
      flipOptions={{
        fallbackAxisSideDirection: 'end',
        fallbackPlacements: ['top-start'],
      }}
      strategyValue="fixed"
      placement="bottom-start"
      triggerElement={triggerElement(setIsOpened)}
      popoverContent={
        <div
          className={twMerge(
            'flex max-h-[150px] min-w-[154px] flex-col overflow-y-auto',
            'rounded-md border-[1px] border-border200',
          )}
        >
          {items.map(item => (
            <ActionMenuButton
              key={item.id}
              label={item.label}
              iconBefore={item.iconBefore}
              iconAfter={item.iconAfter}
              onClick={() => {
                onSelect(item.id)
                setIsOpened(false)
              }}
            />
          ))}
        </div>
      }
      {...popoverProps}
    />
  )
}
